// ══════════════════════════════════════════════════════════════════════════════
//  src/components/teacher/ModalReporteEmpresa.jsx
//  PASAR LISTA Y EVALUAR UNA CLASE DE EMPRESA (EMPRESAS.md, T3.3 · T3.4)
//
//  Por cada persona: asistió / retardo / faltó, los seis criterios de Cambridge
//  (0–5), un comentario QUE LEE LA EMPRESA y una nota interna que solo lee NSL.
//
//  ── LOS DOS TEXTOS, Y POR QUÉ LA PANTALLA INSISTE ────────────────────────────
//  Rob (D8b): el comentario lo lee el jefe del empleado —«sin perjudicarlo ni
//  que se sienta abrumado»—; lo grave o técnico va a NSL «sin que llegue a
//  pegar a la gerencia de la empresa». Si la pantalla no lo dice JUNTO al campo,
//  alguien escribirá en el primero lo que iba en el segundo, y eso ya no se
//  puede des-leer.
//
//  Las reglas las pone el servidor; aquí se avisa antes para no hacer escribir
//  diez evaluaciones y rebotar en la última.
// ══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useClaseEmpresa, useReporteEmpresa } from './useProfesor.js';

const CRITERIOS = [
  ['gramatica', 'Gramática'],
  ['vocabulario', 'Vocabulario'],
  ['pronunciacion', 'Pronunciación'],
  ['fluidez', 'Fluidez y coherencia'],
  ['interaccion', 'Interacción'],
  ['comprension', 'Comprensión'],
  // Rob (D15): «si puso atención, se logró la fluidez, si tiene desempeño».
  ['atencion', 'Atención'],
];

const ASISTENCIA = [
  ['asistio', 'Asistió'],
  ['retardo', 'Retardo'],
  // D15: «estaba ocupado y no pudo concluir su clase». Cuenta como asistencia.
  ['salio_antes', 'Salió antes'],
  ['falto', 'Faltó'],
];

// D15: una falta con motivo NO baja la asistencia que ve la empresa.
const MOTIVOS_FALTA = [
  ['', 'Sin aviso'],
  ['medica', 'Médica (con comprobante)'],
  ['laboral', 'Laboral (su jefe lo necesitó)'],
  ['otra', 'Otra, justificada'],
];

const TOPE = 1000;

function filaDesde(p) {
  return {
    participante_id: p.participante_id,
    nombre: p.nombre,
    asistencia: p.asistencia || null,
    minutos_retardo: p.minutos_retardo ?? '',
    minutos_presente: p.minutos_presente ?? '',
    motivo_falta: p.motivo_falta || '',
    ...Object.fromEntries(CRITERIOS.map(([k]) => [k, p[k] ?? null])),
    comentario_empresa: p.comentario_empresa || '',
    nota_interna_nsl: p.nota_interna_nsl || '',
    requiere_atencion_nsl: Boolean(p.requiere_atencion_nsl),
  };
}

/** El primer problema de una fila, en palabras, o null. */
function problemaDe(f, duracion) {
  if (!f.asistencia) return 'falta marcar la asistencia';
  if (f.asistencia === 'retardo') {
    const m = Number(f.minutos_retardo);
    if (!Number.isInteger(m) || m < 1 || m > duracion) return `los minutos de retardo van de 1 a ${duracion}`;
  }
  if (f.asistencia === 'salio_antes') {
    const m = Number(f.minutos_presente);
    if (!Number.isInteger(m) || m < 1 || m >= duracion) return `di cuántos minutos estuvo (de 1 a ${duracion - 1})`;
  }
  if (f.asistencia !== 'falto') {
    const sin = CRITERIOS.filter(([k]) => f[k] === null || f[k] === undefined);
    if (sin.length) return `falta puntuar ${sin.map(([, t]) => t.toLowerCase()).join(', ')}`;
  }
  if (f.requiere_atencion_nsl && f.nota_interna_nsl.trim().length < 5) {
    return 'explica en la nota interna qué requiere atención';
  }
  return null;
}

export default function ModalReporteEmpresa({ claseId, onCerrar }) {
  const q = useClaseEmpresa(claseId);
  const guardar = useReporteEmpresa();
  const [tema, setTema] = useState('');
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (q.data && filas === null) {
      setTema(q.data.tema_visto || '');
      setFilas(q.data.lista.map(filaDesde));
    }
  }, [q.data, filas]);

  const soloLectura = q.data?.estado_reporte === 'cerrado';
  const duracion = q.data?.duracion_min || 60;

  const problemas = useMemo(() => (filas || [])
    .map((f) => [f.nombre, problemaDe(f, duracion)])
    .filter(([, p]) => p), [filas, duracion]);

  const cambia = (i, cambios) => setFilas((fs) => fs.map((f, j) => (j === i ? { ...f, ...cambios } : f)));

  const entregar = async () => {
    setError('');
    if (tema.trim().length < 3) { setError('Escribe el tema que se vio en la clase.'); return; }
    if (problemas.length) { setError(`${problemas[0][0]}: ${problemas[0][1]}.`); return; }
    try {
      await guardar.mutateAsync({
        claseId,
        reporte: {
          tema_visto: tema.trim(),
          participantes: filas.map((f) => ({
            participante_id: f.participante_id,
            asistencia: f.asistencia,
            minutos_retardo: f.asistencia === 'retardo' ? Number(f.minutos_retardo) : null,
            minutos_presente: f.asistencia === 'salio_antes' ? Number(f.minutos_presente) : null,
            motivo_falta: f.asistencia === 'falto' ? (f.motivo_falta || null) : null,
            ...Object.fromEntries(CRITERIOS.map(([k]) => [k, f.asistencia === 'falto' ? null : f[k]])),
            comentario_empresa: f.comentario_empresa.trim() || null,
            nota_interna_nsl: f.nota_interna_nsl.trim() || null,
            requiere_atencion_nsl: f.requiere_atencion_nsl,
          })),
        },
      });
      onCerrar(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const c = q.data;
  return (
    <div className="pp-overlay" role="presentation">
      <div className="pp-modal pp-modal-ancho" role="dialog" aria-modal="true" aria-label="Reporte de la clase"
        data-prueba="reporte-empresa">
        {q.isLoading && <div className="pp-cargando">Abriendo la lista…</div>}
        {q.isError && (
          <>
            <div className="pp-error">{q.error?.message}</div>
            <div className="pp-acciones"><button className="pp-btn" onClick={() => onCerrar(false)}>Cerrar</button></div>
          </>
        )}

        {c && filas && (
          <>
            <div className="pp-modal-t">{soloLectura ? 'Reporte de la clase' : 'Pasar lista y evaluar'}</div>
            <div className="pp-modal-s">
              {c.empresa} · {c.grupo ? `${c.grupo.nombre} · ${c.grupo.nivel}` : c.formato} ·{' '}
              {new Date(`${c.fecha}T12:00:00`).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}{' '}
              a las {c.hora} · {c.duracion_min} min
            </div>

            {soloLectura && (
              <div className="pp-privado">
                <span aria-hidden="true">🔒</span>
                <span>Pasaron más de 7 días: la empresa ya lo leyó. Si hay que corregir algo, escribe a gerencia.</span>
              </div>
            )}

            <div className="pp-campo">
              <label className="pp-label" htmlFor="pp-tema">Tema que se vio</label>
              <input id="pp-tema" className="pp-input pp-input-cuadro" maxLength={300} disabled={soloLectura}
                value={tema} onChange={(e) => setTema(e.target.value)} placeholder="Ej. Presentaciones en reuniones" />
            </div>

            {!soloLectura && filas.length > 1 && (
              <button className="pp-btn" style={{ marginBottom: 14 }}
                onClick={() => setFilas((fs) => fs.map((f) => (f.asistencia ? f : { ...f, asistencia: 'asistio' })))}>
                Marcar «asistió» a quien falte por marcar
              </button>
            )}

            {filas.map((f, i) => (
              <section className="pp-persona" key={f.participante_id} data-participante={f.nombre}>
                <div className="pp-persona-h">
                  <div className="pp-persona-n">{f.nombre}</div>
                  <div className="pp-opciones pp-asis" role="radiogroup" aria-label={`Asistencia de ${f.nombre}`}>
                    {ASISTENCIA.map(([k, t]) => (
                      <button key={k} type="button" role="radio" aria-checked={f.asistencia === k}
                        disabled={soloLectura}
                        className={`pp-opcion${f.asistencia === k ? ' pp-on' : ''}`}
                        onClick={() => cambia(i, { asistencia: k })}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {f.asistencia === 'retardo' && (
                  <div className="pp-fila-extra">
                    <label className="pp-label" style={{ margin: 0 }} htmlFor={`ret-${f.participante_id}`}>Minutos de retardo</label>
                    <input id={`ret-${f.participante_id}`} className="pp-input pp-input-corto" type="number" min={1}
                      max={duracion} disabled={soloLectura} value={f.minutos_retardo}
                      onChange={(e) => cambia(i, { minutos_retardo: e.target.value })} />
                  </div>
                )}

                {f.asistencia === 'salio_antes' && (
                  <div className="pp-fila-extra">
                    <label className="pp-label" style={{ margin: 0 }} htmlFor={`pre-${f.participante_id}`}>Minutos que estuvo</label>
                    <input id={`pre-${f.participante_id}`} className="pp-input pp-input-corto" type="number" min={1}
                      max={duracion - 1} disabled={soloLectura} value={f.minutos_presente}
                      onChange={(e) => cambia(i, { minutos_presente: e.target.value })} />
                  </div>
                )}

                {f.asistencia === 'falto' && (
                  <div className="pp-fila-extra">
                    <label className="pp-label" style={{ margin: 0 }} htmlFor={`mot-${f.participante_id}`}>¿Por qué faltó?</label>
                    <select id={`mot-${f.participante_id}`} className="pp-input pp-input-cuadro" style={{ flex: '1 1 220px', width: 'auto' }}
                      disabled={soloLectura} value={f.motivo_falta} onChange={(e) => cambia(i, { motivo_falta: e.target.value })}>
                      {MOTIVOS_FALTA.map(([k, t]) => <option key={k || 'sin'} value={k}>{t}</option>)}
                    </select>
                  </div>
                )}

                {f.asistencia && f.asistencia !== 'falto' && (
                  <>
                    <div className="pp-criterios">
                      {CRITERIOS.map(([k, t]) => (
                        <div key={k} className="pp-criterio">
                          <div className="pp-criterio-t"><span>{t}</span><b>{f[k] ?? '—'}</b></div>
                          <div className="pp-escala pp-mini" role="radiogroup" aria-label={`${t} de ${f.nombre}`}>
                            {[0, 1, 2, 3, 4, 5].map((n) => (
                              <button key={n} type="button" role="radio" aria-checked={f[k] === n}
                                disabled={soloLectura}
                                className={`pp-punto${f[k] === n ? ' pp-on' : ''}`}
                                onClick={() => cambia(i, { [k]: n })}>
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="pp-escala-pies" style={{ marginTop: 6 }}>
                      <span>0 · no lo demuestra</span><span>5 · dominio para su nivel</span>
                    </div>
                  </>
                )}

                {f.asistencia && (
                  <div className="pp-notas">
                    <div>
                      <label className="pp-label" htmlFor={`com-${f.participante_id}`}>Comentario para la empresa</label>
                      <textarea id={`com-${f.participante_id}`} className="pp-area" maxLength={TOPE} disabled={soloLectura}
                        value={f.comentario_empresa} onChange={(e) => cambia(i, { comentario_empresa: e.target.value })}
                        placeholder="Lo que se observa en clase y cómo puede mejorar." />
                      <div className="pp-ayuda">👀 <b>Lo lee su jefe.</b> Escribe lo observable y constructivo.</div>
                    </div>
                    <div>
                      <label className="pp-label" htmlFor={`int-${f.participante_id}`}>Nota interna para NSL</label>
                      <textarea id={`int-${f.participante_id}`} className="pp-area" maxLength={TOPE} disabled={soloLectura}
                        value={f.nota_interna_nsl} onChange={(e) => cambia(i, { nota_interna_nsl: e.target.value })}
                        placeholder="Lo delicado o técnico. Opcional." />
                      <div className="pp-ayuda">🔒 <b>Solo la lee gerencia de NSL.</b> La empresa nunca la ve.</div>
                    </div>
                  </div>
                )}

                {f.asistencia && (
                  <label className="pp-check">
                    <input type="checkbox" disabled={soloLectura} checked={f.requiere_atencion_nsl}
                      onChange={(e) => cambia(i, { requiere_atencion_nsl: e.target.checked })} />
                    Requiere atención de NSL
                  </label>
                )}
              </section>
            ))}

            {error && <div className="pp-error" role="alert">{error}</div>}
            {!soloLectura && !error && problemas.length > 0 && (
              <div className="pp-ayuda" style={{ marginTop: 12 }}>
                Falta completar a {problemas.length} {problemas.length === 1 ? 'persona' : 'personas'}.
              </div>
            )}

            <div className="pp-acciones">
              <button className="pp-btn" onClick={() => onCerrar(false)}>{soloLectura ? 'Cerrar' : 'Cancelar'}</button>
              {!soloLectura && (
                <button className="pp-btn pp-btn-ac" disabled={guardar.isPending} onClick={entregar}>
                  {guardar.isPending ? 'Guardando…' : (c.estado_reporte === 'entregado' ? 'Guardar corrección' : 'Entregar reporte')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
